import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useAppDispatch } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FilterListIcon from '@mui/icons-material/FilterList';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { AddMaterialGradeModal } from './AddMaterialGradeModal';
import { EditMaterialGradeModal } from './EditMaterialGradeModal';
import { ImportMaterialGradeModal } from './ImportMaterialGradeModal';
import CustomStyledDataGrid from 'components/MuiDataGrid/CustomStyledDataGrid';
import { type GridColDef } from '@mui/x-data-grid';
import MaterialGradeAPI from 'api/material-grade.api';
import { type MaterialCategoryRecord } from 'views/DispatchImporters/CategoryManagement/types';
import {
  type MaterialGradeRecord,
  type MaterialGradePayload,
  type BulkCreateResult,
  type MakeModelRecord,
} from './types';

interface Props {
  materialCategories: MaterialCategoryRecord[];
  onRefetchMaterialCategories: () => Promise<void>;
}

const fmtDouble = ({ value }: { value: unknown }) => {
  const n = Number(value);
  return !isNaN(n) ? n.toFixed(2) : '—';
};

export const MaterialGradeTonnageTab = ({
  materialCategories,
  onRefetchMaterialCategories,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const api = useRef(new MaterialGradeAPI()).current;

  const [records, setRecords] = useState<MaterialGradeRecord[]>([]);
  const [makeModelOptions, setMakeModelOptions] = useState<MakeModelRecord[]>(
    []
  );
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<MaterialGradeRecord | null>(
    null
  );
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    make: string;
    model: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState<{
    created: number;
    failedEntries: BulkCreateResult['failedEntries'];
  } | null>(null);
  const [colAnchor, setColAnchor] = useState<HTMLElement | null>(null);
  const [colVisibility, setColVisibility] = useState<Record<string, boolean>>({
    make: true,
    model: true,
    materialCategory: true,
    tonnage: true,
    volume: true,
  });

  const toast = (message: string, type: 'success' | 'error') => {
    dispatch(showNotification({ message, type }));
  };

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getAll();
      setRecords(data);
    } catch {
      toast(t('mg_toast_fetch_failed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [api, t]);

  const fetchMakeModels = useCallback(async () => {
    try {
      const data = await api.getMakeModels();
      setMakeModelOptions(data);
    } catch {
      // non-critical; autocomplete just shows empty
    }
  }, [api]);

  useEffect(() => {
    void fetchRecords();
    void fetchMakeModels();
  }, [fetchRecords, fetchMakeModels]);

  const handleAdd = async (record: MaterialGradeRecord) => {
    try {
      const { id: _id, ...payload } = record;
      await api.create(payload);
      await fetchRecords();
      setAddOpen(false);
      toast(t('mg_toast_added'), 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : t('mg_toast_add_failed'), 'error');
      throw e;
    }
  };

  const handleSave = async (
    id: string,
    patch: Partial<MaterialGradePayload>
  ) => {
    try {
      await api.update(id, patch);
      await fetchRecords();
      setEditRecord(null);
      toast(t('mg_toast_updated'), 'success');
    } catch (e) {
      toast(
        e instanceof Error ? e.message : t('mg_toast_update_failed'),
        'error'
      );
      throw e;
    }
  };

  const handleImport = async (entries: MaterialGradePayload[]) => {
    try {
      const result = await api.bulkCreate(entries);
      await fetchRecords();
      setImportOpen(false);
      const failedEntries = result.failedEntries ?? [];
      if (failedEntries.length > 0) {
        setImportResult({
          created: result.created,
          failedEntries,
        });
        toast(
          t('mg_toast_import_partial', {
            created: result.created,
            failed: result.failed,
          }),
          result.created > 0 ? 'success' : 'error'
        );
      } else {
        toast(t('mg_toast_imported', { count: result.created }), 'success');
      }
    } catch (e) {
      toast(
        e instanceof Error ? e.message : t('mg_toast_import_failed'),
        'error'
      );
    }
  };

  const handleDelete = useCallback(
    (id: string, make: string, model: string) => {
      setDeleteTarget({ id, make, model });
    },
    []
  );

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.remove(deleteTarget.id);
      await fetchRecords();
      toast(
        t('mg_toast_deleted', {
          make: deleteTarget.make,
          model: deleteTarget.model,
        }),
        'success'
      );
    } catch (e) {
      toast(
        e instanceof Error ? e.message : t('mg_toast_delete_failed'),
        'error'
      );
    } finally {
      setDeleteTarget(null);
    }
  };

  const exportExcel = () => {
    const rows = records.map((r) => ({
      [t('mg_col_make')]: r.make,
      [t('mg_col_model')]: r.model,
      [t('mg_col_material_category')]: r.materialCategory,
      [t('mg_col_tonnage')]: r.tonnage,
      [t('mg_col_volume')]: r.volume,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Hauler Payload');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([buf], { type: 'application/octet-stream' }),
      `hauler_payload_${Date.now()}.xlsx`
    );
  };

  const gridWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrap = gridWrapRef.current;
    if (!wrap) return;
    let scroller: HTMLElement | null = null;
    let actionsHeader: HTMLElement | null = null;

    const pin = () => {
      if (!scroller || !actionsHeader) return;
      if (scroller.scrollWidth <= scroller.clientWidth) {
        actionsHeader.style.transform = '';
        return;
      }
      const cw = scroller.clientWidth;
      const hw = actionsHeader.offsetWidth;
      const hl = actionsHeader.offsetLeft;
      const sl = scroller.scrollLeft;
      actionsHeader.style.transform = `translateX(${cw - hw - hl + sl}px)`;
      actionsHeader.style.zIndex = '5';
    };

    const setup = () => {
      scroller = wrap.querySelector<HTMLElement>(
        '.MuiDataGrid-virtualScroller'
      );
      actionsHeader = wrap.querySelector<HTMLElement>(
        '.MuiDataGrid-columnHeader[data-field="actions"]'
      );
      if (scroller && actionsHeader) {
        scroller.addEventListener('scroll', pin);
        pin();
      }
    };
    const timer = setTimeout(setup, 50);
    return () => {
      clearTimeout(timer);
      scroller?.removeEventListener('scroll', pin);
    };
  }, [records.length]);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'make', headerName: t('mg_col_make'), minWidth: 130, flex: 1 },
      {
        field: 'model',
        headerName: t('mg_col_model'),
        minWidth: 130,
        flex: 1,
      },
      {
        field: 'materialCategory',
        headerName: t('mg_col_material_category'),
        minWidth: 160,
        flex: 1.5,
      },
      {
        field: 'tonnage',
        headerName: t('mg_col_tonnage'),
        minWidth: 110,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        valueFormatter: fmtDouble,
      },
      {
        field: 'volume',
        headerName: t('mg_col_volume'),
        minWidth: 110,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        valueFormatter: fmtDouble,
      },
      {
        field: 'actions',
        headerName: t('mg_col_actions'),
        width: 100,
        sortable: false,
        disableColumnMenu: true,
        disableReorder: true,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={t('mg_action_edit')}>
              <IconButton
                size="small"
                onClick={() => {
                  setEditRecord(params.row as MaterialGradeRecord);
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('mg_action_delete')}>
              <IconButton
                size="small"
                onClick={() => {
                  const row = params.row as MaterialGradeRecord;
                  handleDelete(row.id, row.make, row.model);
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main' },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [t, handleDelete]
  );

  const visibleColumns = useMemo(
    () =>
      columns.filter((c) => c.field === 'actions' || colVisibility[c.field]),
    [columns, colVisibility]
  );

  const hasData = records.length > 0;

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Modals */}
      <AddMaterialGradeModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        onAdd={handleAdd}
        makeModelOptions={makeModelOptions}
        materialCategories={materialCategories}
        onRefetchMaterialCategories={onRefetchMaterialCategories}
      />
      <EditMaterialGradeModal
        record={editRecord}
        onClose={() => {
          setEditRecord(null);
        }}
        onSave={handleSave}
        makeModelOptions={makeModelOptions}
        materialCategories={materialCategories}
        onRefetchMaterialCategories={onRefetchMaterialCategories}
      />
      <ImportMaterialGradeModal
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
        }}
        onImport={handleImport}
      />

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => {
          setDeleteTarget(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: (th) => alpha(th.palette.error.main, 0.2),
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            {t('mg_delete_title')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t('mg_delete_body', {
              make: deleteTarget?.make ?? '',
              model: deleteTarget?.model ?? '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              setDeleteTarget(null);
            }}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('mg_cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              void confirmDelete();
            }}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('mg_delete_confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import result failures summary */}
      {importResult !== null && importResult.failedEntries.length > 0 && (
        <Dialog
          open
          onClose={() => {
            setImportResult(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, backgroundImage: 'none' } }}
        >
          <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              {t('mg_import_result_title')}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ px: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('mg_import_result_body', {
                created: importResult.created,
                failed: importResult.failedEntries.length,
              })}
            </Typography>
            {importResult.failedEntries.map((entry, i) => (
              <Typography
                key={i}
                variant="caption"
                color="error.main"
                display="block"
              >
                {`${entry.make} ${entry.model} — ${entry.reason}`}
              </Typography>
            ))}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              onClick={() => {
                setImportResult(null);
              }}
              sx={{ borderRadius: '8px', fontWeight: 600 }}
            >
              {t('mg_close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.5),
          flexShrink: 0,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} color="text.primary">
            {t('mg_toolbar_title')}
          </Typography>
          {hasData && (
            <Typography
              variant="caption"
              sx={{
                px: 1.2,
                py: 0.3,
                borderRadius: '20px',
                bgcolor: (th) => alpha(th.palette.primary.main, 0.1),
                color: 'primary.main',
                fontWeight: 700,
              }}
            >
              {records.length}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={t('mg_toolbar_column_visibility')}>
            <IconButton
              size="small"
              onClick={(e) => {
                setColAnchor(e.currentTarget);
              }}
              sx={{ color: 'text.secondary' }}
            >
              <FilterListIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('mg_toolbar_export')}>
            <span>
              <IconButton
                size="small"
                onClick={exportExcel}
                disabled={!hasData}
                sx={{ color: 'text.secondary' }}
              >
                <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('mg_toolbar_refresh')}>
            <IconButton
              size="small"
              onClick={() => {
                void fetchRecords();
              }}
              disabled={loading}
              sx={{
                color: 'text.secondary',
                animation: loading ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              setImportOpen(true);
            }}
            sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.78rem' }}
          >
            {t('mg_toolbar_import')}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              setAddOpen(true);
            }}
            sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.78rem' }}
          >
            {t('mg_toolbar_add')}
          </Button>
        </Box>
      </Box>

      {/* Column visibility popover */}
      <Popover
        open={!!colAnchor}
        anchorEl={colAnchor}
        onClose={() => {
          setColAnchor(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 2, borderRadius: 2, minWidth: 180 } }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            mb: 1,
            display: 'block',
          }}
        >
          {t('mg_col_visibility_title')}
        </Typography>
        {columns
          .filter((c) => c.field !== 'actions')
          .map((col) => (
            <FormControlLabel
              key={col.field}
              control={
                <Checkbox
                  size="small"
                  checked={colVisibility[col.field]}
                  onChange={(e) => {
                    setColVisibility((prev) => ({
                      ...prev,
                      [col.field]: e.target.checked,
                    }));
                  }}
                />
              }
              label={<Typography variant="body2">{col.headerName}</Typography>}
              sx={{ display: 'flex', mx: 0 }}
            />
          ))}
      </Popover>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading && records.length === 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && !hasData && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (th) => alpha(th.palette.primary.main, 0.1),
                border: '1.5px solid',
                borderColor: (th) => alpha(th.palette.primary.main, 0.3),
                mb: 1,
              }}
            >
              <CategoryOutlinedIcon
                sx={{ fontSize: 32, color: 'primary.main' }}
              />
            </Box>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              {t('mg_empty_title')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ maxWidth: 300, lineHeight: 1.7, mb: 1 }}
            >
              {t('mg_empty_subtitle')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => {
                  setImportOpen(true);
                }}
                sx={{ fontWeight: 600, borderRadius: '8px' }}
              >
                {t('mg_toolbar_import')}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setAddOpen(true);
                }}
                sx={{ fontWeight: 600, borderRadius: '8px', px: 3 }}
              >
                {t('mg_toolbar_add')}
              </Button>
            </Box>
          </Box>
        )}

        {hasData && (
          <Box ref={gridWrapRef} sx={{ height: '100%' }}>
            <CustomStyledDataGrid
              rows={records}
              columns={visibleColumns}
              loading={loading}
              disableRowSelectionOnClick
              getRowHeight={() => 48}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeader[data-field="actions"]': {
                  position: 'sticky',
                  right: 0,
                  bgcolor: 'var(--header-bg-dark)',
                },
                '& .MuiDataGrid-cell[data-field="actions"]': {
                  position: 'sticky',
                  right: 0,
                  bgcolor: 'background.paper',
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
